import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';

import styles from './clientGroups.module.scss';
import { config } from '../../api/config';
import { actions } from '../../state';
import {
  formatFilename,
  formatMoney,
  isEmpty,
  mapServiceTypes,
} from '../../utils';
import { Button, ButtonDownload, Header, Title } from '../../components';

const ClientGroupDetails = ({
  dispatch,
  match: { params },
  clientGroups: { selectedGroup, loading },
  locations: { locations },
  memberships: { memberships },
  user,
  clientExport,
  xlsLoading,
}) => {
  const { id } = params;
  const [hasRequested, setHasRequested] = useState(false);

  const fetchGroup = useCallback(
    groupId => {
      dispatch(actions.clientGroups.getClientGroupAction(groupId));
    },
    [dispatch]
  );

  const clearGroups = useCallback(() => {
    dispatch(actions.clientGroups.clearClientGroupsAction());
  }, [dispatch]);

  useEffect(() => {
    fetchGroup(id);
    return () => clearGroups();
  }, [fetchGroup, clearGroups, id]);

  useEffect(() => {
    dispatch(
      actions.locations.getLocationsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'full_address',
            order: 'asc',
          },
        ],
      })
    );
    dispatch(
      actions.memberships.getMembershipsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'title_lt',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (loading) {
      setHasRequested(true);
    }
  }, [loading]);

  useEffect(() => {
    dispatch(actions.clients.setExportIdentifierAction({}));
  }, []);

  const group = isEmpty(selectedGroup) ? null : selectedGroup;
  const showNotFound = hasRequested && !loading && !group;

  const formatMoneyOrDash = value =>
    value === null || value === undefined || value === ''
      ? '-'
      : formatMoney(value);

  const summarySections = useMemo(() => {
    if (!group) return [];
    const isBusinessGroup =
      group.client_type === 92 ||
      group.client_type === 'business' ||
      group.clientType === 'business';
    const activityRows = [];
    const activityDateFrom =
      group.activity_date_from ?? group.activity?.dateFrom;
    const activityDateTo = group.activity_date_to ?? group.activity?.dateTo;
    const activityDays =
      group.activity_days_from_today ?? group.activity?.daysFromToday;
    if (activityDateFrom) {
      activityRows.push({ label: 'Data nuo', value: activityDateFrom });
    }
    if (activityDateTo) {
      activityRows.push({ label: 'Data iki', value: activityDateTo });
    }
    if (activityDays) {
      activityRows.push({
        label: 'Dienų sk. nuo šiandien',
        value: activityDays,
      });
    }
    if (activityRows.length === 0) {
      activityRows.push({ label: 'Aktyvumas', value: '-' });
    }

    const paymentMethods = group.paymentMethods || {};
    const paymentList = group.payment_methods || [];
    const paymentRows = [
      {
        label: 'Apple Pay',
        value:
          paymentList.includes(483) || paymentMethods.applePay ? 'Taip' : 'Ne',
      },
      {
        label: 'Google Pay',
        value:
          paymentList.includes(482) || paymentMethods.googlePay ? 'Taip' : 'Ne',
      },
      {
        label: 'Revolut Pay',
        value:
          paymentList.includes(484) || paymentMethods.revolutPay
            ? 'Taip'
            : 'Ne',
      },
      {
        label: 'Kreditinė kortelė',
        value:
          paymentList.includes(481) || paymentMethods.creditCard
            ? 'Taip'
            : 'Ne',
      },
      {
        label: 'Bankai',
        value:
          paymentList.includes(485) || paymentMethods.banks ? 'Taip' : 'Ne',
      },
    ].map(row => ({
      ...row,
      tone: row.value === 'Taip' ? 'success' : 'error',
    }));

    const locationMap = new Map(
      (locations || []).map(location => [
        String(location.id),
        location.full_address || location.address || String(location.id),
      ])
    );
    const membershipMap = new Map(
      (memberships || []).map(membership => [
        String(membership.id),
        membership.title_lt ||
          membership.title_en ||
          membership.title_ru ||
          String(membership.id),
      ])
    );
    const locationIds =
      group.location_ids && group.location_ids.length
        ? group.location_ids
        : group.location_id || group.address
        ? [group.location_id || group.address]
        : [];
    const membershipIds =
      group.membership_ids && group.membership_ids.length
        ? group.membership_ids
        : group.membership_id || group.plan
        ? [group.membership_id || group.plan]
        : [];
    const locationValues = locationIds
      .map(value => locationMap.get(String(value)) || String(value))
      .filter(Boolean);
    const membershipValues = membershipIds
      .map(value => membershipMap.get(String(value)) || String(value))
      .filter(Boolean);

    return [
      {
        title: 'Aktyvumas dienomis',
        rows: activityRows,
      },
      {
        title: 'Plovykla ir planas',
        rows: [
          {
            label: 'Adresas',
            ...(locationValues.length
              ? { values: locationValues }
              : { value: '-' }),
          },
          {
            label: 'Aktyvus planas',
            ...(membershipValues.length
              ? { values: membershipValues }
              : { value: '-' }),
          },
          {
            label: isBusinessGroup
              ? 'Sąskaitos suma'
              : 'E-piniginės pildymas, Eur',
            values: [
              `Nuo ${
                (group.virtual_top_up_from !== undefined &&
                  group.virtual_top_up_from !== null) ||
                (group.invoiceFrom !== undefined && group.invoiceFrom !== null)
                  ? formatMoney(group.virtual_top_up_from ?? group.invoiceFrom)
                  : '-'
              }`,
              `Iki ${
                (group.virtual_top_up_to !== undefined &&
                  group.virtual_top_up_to !== null) ||
                (group.invoiceTo !== undefined && group.invoiceTo !== null)
                  ? formatMoney(group.virtual_top_up_to ?? group.invoiceTo)
                  : '-'
              }`,
            ],
          },
        ],
      },
      {
        title: 'Mokėjimo metodai',
        rows: paymentRows,
      },
    ];
  }, [group, locations, memberships]);

  const serviceGroups = useMemo(() => {
    const mapServiceLabel = value => {
      if (value === '' || value === null || value === undefined) {
        return 'Pasirinkti';
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        const mapped = mapServiceTypes(value);
        return mapped === '-' ? String(value) : mapped;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return 'Pasirinkti';
        const numeric = Number(trimmed);
        if (Number.isFinite(numeric)) {
          const mapped = mapServiceTypes(numeric);
          return mapped === '-' ? trimmed : mapped;
        }
        return (
          {
            'self-service': 'Savitarna',
            'tire-blackener': 'Padangų juodinimas',
          }[trimmed] || trimmed
        );
      }
      return String(value);
    };
    if (Array.isArray(group?.service_groups)) {
      return group.service_groups.map((rows, index) => ({
        title: `Panaudotų paslaugų grupė ${index + 1}`,
        entries: rows.map(row => ({
          service: mapServiceLabel(row.service_type),
          amountFrom: formatMoneyOrDash(row.sum_from),
          amountTo: formatMoneyOrDash(row.sum_to),
          usageCount:
            row.use_count !== undefined && row.use_count !== null
              ? row.use_count
              : '-',
        })),
      }));
    }
    if (Array.isArray(group?.serviceGroups)) {
      return group.serviceGroups.map((item, index) => ({
        title:
          item.title?.replace(
            /Used services group (\d+)/i,
            'Panaudotų paslaugų grupė $1'
          ) || `Panaudotų paslaugų grupė ${index + 1}`,
        entries: item.rows.map(row => ({
          service: mapServiceLabel(row.service),
          amountFrom: row.amountFrom ? `${row.amountFrom} Eur` : '-',
          amountTo: row.amountTo ? `${row.amountTo} Eur` : '-',
          usageCount: row.usageCount ?? '-',
        })),
      }));
    }
    return [];
  }, [group]);

  const handleExport = () => {
    const filterBody = {
      filter: {
        client_group_ids: [Number(id)],
      },
    };

    const filename = formatFilename(config.PAGES.clients);
    dispatch(
      actions.clients.exportClientGroupsInnerAsyncAction(filterBody, filename)
    );
  };
  const renderRows = rows => (
    <div className={styles.rows}>
      {rows.map(({ label, value, values, tone }, index) => {
        const valueClassName = cn(styles.value, tone && styles[tone]);

        return (
          <div className={styles.row} key={`${label}-${index}`}>
            <span className={styles.label}>{label}</span>
            {values ? (
              <div className={styles.valueStack}>
                {values.map((item, valueIndex) => (
                  <span className={styles.value} key={`${label}-${valueIndex}`}>
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <span className={valueClassName}>{value}</span>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <section className={styles.page}>
      <Header size="lg">
        <div className={styles.titleWrapper}>
          <Title>{group?.name || 'Klientų grupė'}</Title>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.iconButton}>
            <ButtonDownload
              type="xls"
              loading={xlsLoading}
              onClick={handleExport}
            />
          </div>
          <Button
            color="primary"
            url={config.APP_PATHS.clientGroupsEdit(id)}
            classes={{ button: styles.actionButton }}
          >
            Redaguoti
          </Button>
        </div>
      </Header>

      {loading && <div className={styles.tableEmpty}>Kraunama...</div>}
      {showNotFound && (
        <div className={styles.tableEmpty}>Klientų grupė nerasta.</div>
      )}
      {!loading && group && (
        <>
          <div className={styles.card}>
            {summarySections.map((section, index) => (
              <React.Fragment key={section.title}>
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>{section.title}</h3>
                  {renderRows(section.rows)}
                </div>
                {index < summarySections.length - 1 && (
                  <div className={styles.divider} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className={cn(styles.card, styles.cardStack)}>
            {serviceGroups.map((groupItem, index) => (
              <React.Fragment key={groupItem.title}>
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>{groupItem.title}</h3>
                  <div className={styles.entries}>
                    {groupItem.entries.map((entry, entryIndex) => (
                      <div
                        className={styles.entry}
                        key={`${groupItem.title}-${entryIndex}`}
                      >
                        {renderRows([
                          { label: 'Paslauga', value: entry.service },
                          {
                            label: 'Suma nuo, EUR',
                            value: entry.amountFrom,
                          },
                          { label: 'Suma iki, EUR', value: entry.amountTo },
                          { label: 'Panaudojimų sk.', value: entry.usageCount },
                        ])}
                      </div>
                    ))}
                  </div>
                </div>
                {index < serviceGroups.length - 1 && (
                  <div className={styles.divider} />
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const mapStateToProps = state => ({
  clientGroups: state.clientGroups,
  locations: state.locations,
  memberships: state.memberships,
  user: state.auth.user,
  clientExport: state.clients.clientExport,
  xlsLoading: state.download.xlsLoading,
});

ClientGroupDetails.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  clientGroups: PropTypes.shape({
    selectedGroup: PropTypes.object.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  locations: PropTypes.shape({
    locations: PropTypes.array.isRequired,
  }).isRequired,
  memberships: PropTypes.shape({
    memberships: PropTypes.array.isRequired,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  clientExport: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    filename: PropTypes.string,
  }).isRequired,
  xlsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(ClientGroupDetails);
